import { ANALYTICS_REPORTING_MODE_SEARCH_PARAM } from '~/utils/analyticsReportingMode';
import { CUSTOM_REPORTS_SCOPE_PARAM } from './analyticsStatsCustomReportsScope';

/** Date and entity filter params shared across analytics stats report pages. */
export const ANALYTICS_STATS_FILTER_PARAMS = ['from', 'to', 'period', 'cid', 'pid'] as const;

/** Home-only UI params preserved when navigating back to analytics home. */
export const ANALYTICS_STATS_HOME_PARAMS = ['tab', CUSTOM_REPORTS_SCOPE_PARAM] as const;

/**
 * Staff Classic Genie pre-seed: keep the selected Genie 2.0 universe across New / Run / Edit / back.
 * Harmless on client routes (param is never present). Live Genie 2.0 strips it at the layout.
 */
export const ANALYTICS_STATS_STAFF_PRESEED_PARAMS = [
  ANALYTICS_REPORTING_MODE_SEARCH_PARAM,
] as const;

export type AnalyticsStatsSearchMode = 'filters' | 'home';

function normalizeSearch(search: string): string {
  if (!search) return '';
  return search.startsWith('?') ? search.slice(1) : search;
}

/** Copy named query params from a search string (supports multi-value params). */
export function pickSearchParams(search: string, keys: readonly string[]): URLSearchParams {
  const source = new URLSearchParams(normalizeSearch(search));
  const picked = new URLSearchParams();
  keys.forEach((key) => {
    source.getAll(key).forEach((value) => {
      picked.append(key, value);
    });
  });
  return picked;
}

function appendQuery(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Build an analytics stats href, preserving filter and (optionally) home query params.
 *
 * @param path - Absolute or relative path (e.g. `/client/1/analytics/stats/view/sv_1`)
 * @param search - Current `location.search` (with or without leading `?`)
 * @param mode - `filters` for report-to-report nav; `home` includes tab/scope for back-to-home
 * @param setParams - Optional params to set on the result (e.g. force `tab=custom` after save)
 */
export function buildAnalyticsStatsHref(
  path: string,
  search: string,
  mode: AnalyticsStatsSearchMode = 'filters',
  setParams?: Record<string, string>,
): string {
  const keys =
    mode === 'home'
      ? [
          ...ANALYTICS_STATS_FILTER_PARAMS,
          ...ANALYTICS_STATS_HOME_PARAMS,
          ...ANALYTICS_STATS_STAFF_PRESEED_PARAMS,
        ]
      : [...ANALYTICS_STATS_FILTER_PARAMS, ...ANALYTICS_STATS_STAFF_PRESEED_PARAMS];
  const params = pickSearchParams(search, keys);
  if (setParams) {
    Object.entries(setParams).forEach(([key, value]) => {
      params.set(key, value);
    });
  }
  return appendQuery(path, params);
}

/** Preserve analytics stats filter params when merging onto a new path. */
export function keepAnalyticsStatsFilterParams(path: string, search: string): string {
  return buildAnalyticsStatsHref(path, search, 'filters');
}

/** Preserve filters plus home tab/scope when linking back to analytics home. */
export function keepAnalyticsStatsHomeParams(path: string, search: string): string {
  return buildAnalyticsStatsHref(path, search, 'home');
}
